import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { Outcome } from "@financial-advisory/data-access/api-types";
import {
  clearActiveQueryState,
  selectActiveClause,
  selectEvaluationExamplesByPaneId,
  useEvaluateQueryMutation,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { selectActiveQueryState } from "@financial-advisory/data-access/financial-advisory-api-service";
import { selectActivePaneId } from "@financial-advisory/data-access/financial-advisory-api-service";
import { SecondaryButton } from "@financial-advisory/ui/components/buttons";
import { Input } from "@financial-advisory/ui/components/inputs";
import { Spinner } from "@financial-advisory/ui/components/spinners";
import { Combobox } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { IconSend2 } from "@tabler/icons-react";
import { Modal, ModalProps, useModal } from "@unlikelyai-magic/ui/modals";
import { Heading, Paragraph } from "@unlikelyai-magic/ui/typography";
import { cn } from "@unlikelyai-magic/utils";

type Example = {
  id: number;
  name: string;
  value: Outcome;
};

const QueryInput = styled(Input)`
  border-radius: 0;

  &,
  &:focus,
  &:hover {
    outline: none;
    border: none;
    box-shadow: none;
  }
`;

const SubmitButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.colors.text.secondary.default};
`;

type QueryModalProps = ModalProps & {
  title?: string;
  description?: string;
  icon?: ReactNode;
  cancelText?: string;
  confirmText?: string;
};

export const QueryModal = styled(({ className }: QueryModalProps) => {
  const dispatch = useDispatch();
  const { closeModal } = useModal();
  const activeClause = useSelector(selectActiveClause);
  const activePaneId = useSelector(selectActivePaneId);
  const { activeQuery, loading, rejectedRules, error } = useSelector(
    selectActiveQueryState
  );
  const allExamples = useSelector(
    selectEvaluationExamplesByPaneId(activePaneId)
  );
  const [evaluateQuery] = useEvaluateQueryMutation();
  const [query, setQuery] = useState<string>("");

  // Select up to 3 random examples, memoized to avoid re-randomizing
  const displayExamples = useMemo(() => {
    if (allExamples.length <= 3) return allExamples;
    return allExamples.slice(0, 3);
  }, [allExamples]);

  useEffect(() => {
    if (activeClause) return;
    closeModal();
  }, [activeClause, closeModal]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submitQuery(query);
  };

  const submitQuery = (query: string) => {
    if (!activePaneId) return;
    evaluateQuery({ query, pane_id: activePaneId });
  };

  return (
    <Modal
      className={cn(
        className,
        "mx-auto w-full max-w-2xl transform divide-y divide-gray-100 overflow-hidden rounded-xl bg-white shadow-2xl outline outline-1 outline-black/5 transition-all data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      )}
    >
      {/* Content */}
      {activeQuery ? (
          <div className="max-h-96 overflow-y-auto py-2">
            <div className="flex flex-col px-4 py-2">
              <h2 className="text-base font-semibold text-gray-500">Query</h2>
              <Paragraph>{activeQuery}</Paragraph>
            </div>
            <div className="flex flex-col gap-4 px-4 py-2">
              {loading ? (
                <Spinner />
              ) : (
                <>
                  {error && (
                    <div className="flex flex-col gap-2">
                      <Heading level="h5">Error</Heading>
                      <Paragraph>
                        An error occurred while evaluating the query.
                      </Paragraph>
                    </div>
                  )}
                  {rejectedRules.length > 0 && !error && (
                    <>
                      <h2 className="text-base font-semibold text-gray-500">
                        Results
                      </h2>
                      <div className="flex flex-col gap-2">
                        {rejectedRules.map((rejectedRule) => (
                          <div key={rejectedRule.ruleId}>
                            <Heading level="h5">{rejectedRule.ruleId}</Heading>
                            <Paragraph>{rejectedRule.reasoning}</Paragraph>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  <div>
                    <SecondaryButton
                      onClick={() => dispatch(clearActiveQueryState())}
                    >
                      Clear
                    </SecondaryButton>
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <Combobox
            onChange={(item?: Example) => {
              if (item) {
                submitQuery(item.name);
              }
            }}
          >
            <form onSubmit={handleSubmit} className="flex flex-row">
              <MagnifyingGlassIcon
                className="pointer-events-none ml-4 size-5 self-center text-gray-400"
                aria-hidden="true"
              />
              <QueryInput
                placeholder={"Run a bot query"}
                onChange={(event) => setQuery(event.target.value)}
                value={query}
                className="h-14 w-full pr-4 pl-11 text-base text-gray-900"
              />
              <div className="flex items-center justify-center mr-4">
                <SubmitButton
                  size="sm"
                  icon={<IconSend2 />}
                  onClick={() => submitQuery(query)}
                >
                  Send
                </SubmitButton>
              </div>
            </form>
            {displayExamples.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Example queries:
                </h3>
                <div className="flex flex-col gap-2">
                  {displayExamples.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => submitQuery(example.scenario)}
                      className="text-left px-3 py-2 text-sm text-gray-700 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors"
                    >
                      {example.scenario}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Combobox>
        )}
    </Modal>
  );
})`
  border-radius: 0.75rem;
`;
